<script>

import {useLandingPageStore} from "../../../stores/landingPage";
import BaseModal from "../BaseModal.vue";
import { VueDraggable} from "vue-draggable-plus";
import Icon from "../Icon.vue";
import {ApplicationHelper} from "../../../helpers/ApplicationHelper";

export default {
  name: "CampaignSorter",

  components: {
    Icon,
    VueDraggable,
    BaseModal
  },
  props: {
    donationTargets: {
      type: Array,
      required: true
    },
    order: {
      type: Array,
      required: false
    }
  },
  emits: ['close', 'save'],

  setup() {
    const landingStore = useLandingPageStore();

    return {
      landingStore
    };
  },

  data() {
    return {
      campaigns: [],
      dragInProgress: false,
      sortOption: null
    };
  },
  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
    },
    sortedTargets() {
      if(this.order && this.order.length > 0) {
        return ApplicationHelper.sortCampaignsByCustomOrder(this.donationTargets, this.order);
      } else {
        return this.donationTargets;
      }
    },
  },

  mounted() {
    this.cloneCampaigns();
  },
  methods: {
    save() {
      this.$emit('save', this.campaigns);
      this.$emit('close');
    },
    reset() {
      this.cloneCampaigns();
    },

    // Clones the list from the data source
    cloneCampaigns() {
      this.campaigns = JSON.parse(JSON.stringify(this.sortedTargets)); // Cloning the original list
    },
    sortCampaigns() {
      switch (this.sortOption) {
        case 'end_date':
          this.sortByEndDate();
          break;
        case 'name':
          this.sortByName();
          break;
        case 'closest_goal':
          this.sortByGoal();
          break;
        case 'furthest_goal':
          this.sortByGoal(false);
          break;
      }
    },
    sortByEndDate() {
      // First, separate campaigns with and without end_date
      const withEndDate = this.campaigns.filter(c => c.end_date);
      const withoutEndDate = this.campaigns.filter(c => !c.end_date);

      // Sort campaigns with end_date (ascending - soonest first)
      withEndDate.sort((a, b) => {
        const dateA = new Date(a.end_date);
        const dateB = new Date(b.end_date);
        return dateA - dateB;
      });

      // Combine arrays, with campaigns without end_date at the end
      this.campaigns = [...withEndDate, ...withoutEndDate];
    },
    sortByName() {
      this.campaigns.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    },

    sortByGoal(closest = true) {
      this.campaigns.sort((a, b) => {
        const aHasGoal = a.donation_target && a.donation_target > 0;
        const bHasGoal = b.donation_target && b.donation_target > 0;

        // Push campaigns without valid goals to the end
        if (!aHasGoal && !bHasGoal) return 0;
        if (!aHasGoal) return 1;
        if (!bHasGoal) return -1;

        const aPercent = a.all_time_contribution_total / a.donation_target;
        const bPercent = b.all_time_contribution_total / b.donation_target;

        // Sort by closeness to 100% if closest is true, otherwise furthest
        if (closest) {
          return Math.abs(1 - aPercent) - Math.abs(1 - bPercent);
        } else {
          return Math.abs(1 - bPercent) - Math.abs(1 - aPercent);
        }
      });
    },
     percent(campaign) {
      if(!campaign.donation_target) return 'NO GOAL';
      const perc = Math.abs(campaign.all_time_contribution_total / campaign.donation_target) * 100;
      return perc.toFixed(2) + "%";
     },

    formatDate(dateString) {
      if (!dateString) return;
      const date = new Date(dateString);
      return date.toLocaleDateString();
    }
  }


};
</script>

<template>
  <base-modal
    :background-dismissible="false"
    @close="$emit('close')"
  >
    <template #header-slot>
      <div class="subtitle-regular">
        Sort Campaigns and Funds
      </div>
    </template>

    <!-- Hidden loading of all the images to keep them in memory, helps with image flickering when the DOM re-renders the list -->
    <div style="display: none;">
      <img
        v-for="campaign in sortedTargets"
        :key="`preload-${campaign.uuid}`"
        :src="campaign.banner_image_data?.thumbnail_url"
      >
    </div>

    <div class="flex-row flex w-full items-end justify-end">
      <button
        class="btn btn-filled mr-2 max-h-10"
        @click="save"
      >
        SAVE
      </button>
      <button
        class="btn btn-outline mr-2 max-h-10"
        @click="reset"
      >
        RESET
      </button>
      <div class="flex flex-col">
        <label
          for="sort-select"
          class="mr-2 font-medium"
        >Sort by:</label>
        <select
          id="sort-select"
          v-model="sortOption"
          class="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          @change="sortCampaigns"
        >
          <option
            disabled
            :value="null"
          >
            Select
          </option>
          <option value="name">
            Name
          </option>
          <option value="end_date">
            Ending Soonest
          </option>
          <option value="closest_goal">
            Closest to goal (%)
          </option>
          <option value="furthest_goal">
            Furthest from goal (%)
          </option>
        </select>
      </div>
    </div>

    <vue-draggable
      v-model="campaigns"
      animation="150"
    >
      <div
        v-for="(campaign, index) in campaigns"
        :key="campaign.uuid"
        class="draggable-card card"
      >
        <div class="flex flex-row justify-between w-full">
          <div class="index-number bg-blue-600 w-6 flex items-center pl-2 text-center text-white font-semibold">
            {{ index + 1 }}
          </div>
          <div class="flex flex-row w-full p-2 justify-between">
            <div class="w-14 h-14">
              <!-- Using background-image instead of img tag -->
              <div
                class="h-full aspect-square bg-cover bg-center bg-gray-400 rounded-sm"
                :style="`background-image: url('${campaign.banner_image_data?.thumbnail_url}')`"
              />
            </div>
            <div class="flex flex-col w-full">
              <div class="subtitle-regular text-center">
                {{ campaign.name }}
              </div>
              <div class="flex flex-row justify-center content-center">
                <!--                <div v-if="campaign.donation_target">-->
                <!--                  {{ percent(campaign)}} Funded-->
                <!--                </div>-->
                <!--                <div v-else> No Goal</div>-->
                <!--                <div class="mx-2">-->
                <!--                  &bullet;-->
                <!--                </div>-->
                <div v-if="campaign.end_date">
                  Ends {{ ApplicationHelper.formatDate(campaign.end_date, {time: false}) }}
                </div>
                <div v-else>
                  No End Date
                </div>
              </div>
            </div>
            <div>
              <icon
                icon="elipses"
              />
            </div>
          </div>
        </div>
      </div>
    </vue-draggable>
  </base-modal>
</template>

<style lang="scss" scoped>
.draggable-card {
  @apply rounded-md overflow-hidden w-full my-2 border;
  cursor: grab;
 }
</style>
